import React, { useState } from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import api from '../api';
import tokens from '../theme/tokens';
import { formatCurrency } from '../utils/image';
import { dataPelada as formatarDataPelada } from '../utils/dataPelada';

const dataPelada = (p) => formatarDataPelada(p?.date, { weekday: 'long', day: '2-digit', month: 'long' });

/**
 * Cartão de check-in do goleiro/árbitro na pelada aberta.
 * `escala`: retorno de /equipe/minha-escala ({ pelada, item }). `onCheckin` recarrega a tela.
 */
const CheckinEquipe = ({ escala, papel, onCheckin }) => {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  if (!escala?.pelada) {
    return (
      <Card sx={{ p: 4, textAlign: 'center' }}>
        <EventBusyRoundedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="h6" sx={{ mt: 1 }}>Nenhuma pelada aberta</Typography>
        <Typography color="text.secondary">Quando a diretoria abrir a pelada, o check-in aparece aqui.</Typography>
      </Card>
    );
  }
  if (!escala.item) {
    return (
      <Card sx={{ p: 4, textAlign: 'center' }}>
        <EventBusyRoundedIcon sx={{ fontSize: 48, color: 'warning.main' }} />
        <Typography variant="h6" sx={{ mt: 1 }}>Você não está escalado</Typography>
        <Typography color="text.secondary">A pelada de {dataPelada(escala.pelada)} está aberta, mas você não está na escala. Fale com a diretoria.</Typography>
      </Card>
    );
  }

  const fazerCheckin = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post('equipe/checkin');
      onCheckin && onCheckin();
    } catch (error) {
      setErro(error.response?.data?.error || 'Não foi possível fazer o check-in.');
    }
    setEnviando(false);
  };

  const feito = Boolean(escala.item.checkinEm);

  return (
    <Card sx={{ overflow: 'hidden' }}>
      <Box sx={{ p: 3, textAlign: 'center', color: '#fff', background: feito ? `linear-gradient(160deg, ${tokens.color.success}, #157a42)` : `linear-gradient(160deg, ${tokens.color.inkSoft}, ${tokens.color.ink})` }}>
        {feito ? <CheckCircleRoundedIcon sx={{ fontSize: 56 }} /> : <HowToRegRoundedIcon sx={{ fontSize: 56 }} />}
        <Typography variant="h5" sx={{ mt: 1 }}>{feito ? 'Check-in feito!' : 'Você está escalado'}</Typography>
        <Typography sx={{ opacity: 0.85, textTransform: 'capitalize' }}>{dataPelada(escala.pelada)} · {escala.pelada.time}</Typography>
      </Box>
      <Stack spacing={2} sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography color="text.secondary">Função</Typography>
          <Typography fontWeight={700}>{papel === 'goleiro' ? 'Goleiro' : 'Árbitro'}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography color="text.secondary">Valor da pelada</Typography>
          <Typography fontWeight={700}>{formatCurrency(escala.item.valor)}</Typography>
        </Stack>
        {feito && (
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">Check-in</Typography>
            <Typography fontWeight={700}>
              {new Date(escala.item.checkinEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              {escala.item.checkinPor && escala.item.checkinPor !== 'próprio' ? ` (por ${escala.item.checkinPor})` : ''}
            </Typography>
          </Stack>
        )}
        {erro && <Alert severity="error">{erro}</Alert>}
        {!feito ? (
          <Button variant="contained" size="large" startIcon={enviando ? <CircularProgress size={20} color="inherit" /> : <HowToRegRoundedIcon />} onClick={fazerCheckin} disabled={enviando} sx={{ py: 1.6 }}>
            Fazer check-in
          </Button>
        ) : (
          <Alert severity="success" variant="outlined">O pagamento desta pelada foi registrado e será feito pela diretoria.</Alert>
        )}
        {escala.colegas?.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary">Equipe da pelada</Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
              {escala.colegas.map((c, i) => (
                <Chip key={i} size="small" color={c.checkinEm ? 'success' : 'default'} variant={c.checkinEm ? 'filled' : 'outlined'} label={`${c.tipo === 'goleiro' ? 'GK' : 'Juiz'} · ${c.nome}`} />
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Card>
  );
};

export default CheckinEquipe;
